import { Component, Input, OnInit, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { util } from '../../../classes/util';
import { ESRI } from '../../../esri-loader-util';
import { LayerService } from '../../../services/layer.service';
import { BaseComponent } from '../../base.component';

const maxTextLayerNameLength = 75;
const initilizeDebounceTime = 50;

let Draw: __esri.DrawConstructor;
let Graphic: __esri.GraphicConstructor;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-draw-text',
    templateUrl: './draw-text.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './draw-text.component.scss']
})
export class DrawTextComponent extends BaseComponent implements OnInit {
    @Input()
    set enabled(enabled: boolean) {
        this._enabled = enabled;
        if (this._enabled) {
            this.setupHandleHiddenLayer();
            this.createPointDrawAction();
        } else {
            this.teardownHandleHiddenLayer();
            this.draw.reset();
        }
    }
    _enabled: boolean;

    @Input()
    symbolProperties: any;
    createLayerForEachGraphic: boolean;

    mapView: NonNullable<__esri.MapView>;
    draw: NonNullable<__esri.Draw>;
    graphicsLayer: NonNullable<__esri.GraphicsLayer>;

    defaultSymbolProperties = {
        type: 'text', // autocasts as TextSymbol
        color: 'white',
        horizontalAlignment: 'left',
        verticalAlignment: 'baseline',
        font: { // autocasts as Font
            size: '12px',
            family: 'Arial',
            weight: 'bold'
        }
    };

    horizontalAlignmentOptions = ['left', 'right', 'center', 'justify'];
    verticalAlignmentOptions = ['baseline', 'top', 'middle', 'bottom'];
    fontSizeOptions = ['2', '4', '6', '8', '10', '12', '14', '16', '18', '20', '22', '24', '30', '32', '36', '40', '50', '64', '72'];

    fontFamilyOptions: { name: string; bold?: boolean; italic?: boolean }[] = [
        { name: 'Arial', bold: true, italic: true },
        { name: 'Avenir Next LT Pro', bold: true, italic: true },
        { name: 'Black Ops One' },
        { name: 'Josefin Sans', italic: true },
        { name: 'Josefin Slab', bold: true, italic: true },
        { name: 'Merriweather', bold: true, italic: true },
        { name: 'Montserrat', bold: true, italic: true },
        { name: 'Noto Sans', bold: true, italic: true },
        { name: 'Old Standard TT', bold: true, italic: true },
        { name: 'Orbitron', bold: true },
        { name: 'Oregano', italic: true },
        { name: 'Oswald', bold: true },
        { name: 'Ubuntu', bold: true, italic: true },
        { name: 'Ubuntu Mono', bold: true, italic: true }
    ];
    get fontFamilies() {
        return this.fontFamilyOptions.map(fontOption => fontOption.name);
    }

    boldDisabled = false;
    italicDisabled = false;

    constructor(
        _vcr: ViewContainerRef,
        private readonly layerService: LayerService
    ) {
        super(_vcr);
    }

    colorPicker: ViewContainerRef;
    @ViewChild('colorPicker') set colorPickerRef(el: ViewContainerRef) { this.colorPicker = el; }

    async ngOnInit() {
        try {
            this.createLayerForEachGraphic = true;
            this.symbolProperties = this.defaultSymbolProperties;
            this.mapView = await this.setupMapViewRef(this.mapView);
            [Draw, Graphic] = await Promise.all([ESRI.Draw, ESRI.Graphic]);
            this.draw = new Draw({ view: this.mapView });
        } catch (err) {
            this.readyProblem(err);
        } finally {
            this.isReady();
        }
    }

    async createPointDrawAction() {
        const action = this.draw.create('point');
        action.on('draw-complete', (evt) => {
            const graphic = this.createPointGraphic(evt.vertices[0]);
            this.addPointGraphic(graphic);
            this.createPointDrawAction();
        });
    }

    private createPointGraphic(coordinates) {
        const point = {
            type: 'point', // autocasts as /Point
            x: coordinates[0],
            y: coordinates[1],
            spatialReference: this.mapView.spatialReference
        };

        const graphic = new Graphic({
            geometry: point,
            symbol: this.symbolProperties
        });

        return graphic;
    }

    private async addPointGraphic(graphic: __esri.Graphic) {
        if (!this.graphicsLayer || this.createLayerForEachGraphic) {
            let title = (<__esri.TextSymbol>graphic.symbol).text;
            if (title.length > maxTextLayerNameLength) {
                title = title.slice(0, maxTextLayerNameLength);
                title = title.slice(0, title.lastIndexOf(' ')) + '...';
            }
            this.graphicsLayer = await this.layerService.addGraphicsLayer({ title, graphicLayer: 'text' });
        }

        this.graphicsLayer.add(graphic);
    }

    fontFamilyChanged() {
        const selectedFont = this.fontFamilyOptions.find(fontOption => fontOption.name === this.symbolProperties.font.family);
        if (selectedFont) {
            this.boldDisabled = !selectedFont.bold;
            this.italicDisabled = !selectedFont.italic;
            if (this.boldDisabled && this.symbolProperties.font.weight === 'bold') {
                this.toggleBold();
            }
            if (this.italicDisabled && this.symbolProperties.font.style === 'italic') {
                this.toggleItalic();
            }
        } else {
            // Something wrong?
            this.boldDisabled = false;
            this.italicDisabled = false;
        }
    }

    // There are more options for these styles, but for now we're going to match MC1
    // https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-Font.html

    public toggleBold() {
        this.symbolProperties.font.weight = this.symbolProperties.font.weight === 'bold' ? 'normal' : 'bold';
    }

    public toggleItalic() {
        this.symbolProperties.font.style = this.symbolProperties.font.style === 'italic' ? 'normal' : 'italic';
    }

    public toggleUnderline() {
        this.symbolProperties.font.decoration = this.symbolProperties.font.decoration === 'underline' ? 'normal' : 'underline';
    }

    initializing = false;
    private readonly listners: IHandle[] = [];
    private setupHandleHiddenLayer() {
        this.initializing = true;
        const stopInitialCheck = util.debounce(() => { this.initializing = false; }, initilizeDebounceTime);
        this.listners.push(this.layerService.mapRef.allLayers.on('before-add', (event) => {
            if (this.initializing && event.item.listMode === 'hide') {
                event.item.set('hideInLegend', true);
                event.item.set('drawTextToolGeneratedLayer', true);
                stopInitialCheck();
            }
        }));
        this.listners.push(this.layerService.mapRef.allLayers.on('after-remove', (event) => {
            if (event.item.get<boolean>('drawTextToolGeneratedLayer')) {
                this.initializing = true;
            }
        }));
    }
    private teardownHandleHiddenLayer() {
        while (this.listners.length) {
            this.listners.pop().remove();
        }
    }
}
